import { useState } from "react";
import { Link, useParams } from "react-router";
import { Aviso, Botao, CampoLongo } from "../componentes/ui";
import { CascaPublica } from "../componentes/layout";
import { ErroDaApi } from "../nucleo/api";
import { escalaDa, usePesquisaPublica, useResponderPesquisa } from "../nucleo/pesquisas";
import { useTitulo } from "../nucleo/titulo";

/**
 * A pesquisa de satisfação pelo link do e-mail — sem login. O token do endereço é a autorização
 * e vale uma resposta só; a tela não mostra nada do chamado nem da empresa, só a pergunta.
 */
export function ResponderPesquisa() {
  useTitulo("Pesquisa de satisfação");
  const { token = "" } = useParams();
  const pesquisa = usePesquisaPublica(token);
  const responder = useResponderPesquisa(token);
  const [nota, setNota] = useState<number | null>(null);
  const [comentario, setComentario] = useState("");

  const rodape = <Link to="/">Página inicial</Link>;

  if (pesquisa.isPending) {
    return (
      <CascaPublica titulo="Pesquisa de satisfação" rodape={rodape}>
        <p role="status">Abrindo a pesquisa…</p>
      </CascaPublica>
    );
  }

  if (pesquisa.isError) {
    const naoExiste = pesquisa.error instanceof ErroDaApi && pesquisa.error.status === 404;
    return (
      <CascaPublica
        titulo={naoExiste ? "Este link não vale" : "Não foi possível abrir a pesquisa"}
        rodape={rodape}
      >
        <Aviso tom="atencao" data-testid="pesquisa-indisponivel">
          {naoExiste
            ? "O link pode ter sido cortado pelo programa de e-mail. Copie o endereço inteiro da mensagem e cole na barra do navegador."
            : "Tente de novo em instantes."}
        </Aviso>
      </CascaPublica>
    );
  }

  const dados = pesquisa.data;

  if (dados.situacao === "respondida" || responder.isSuccess) {
    return (
      <CascaPublica titulo="Obrigado pela sua resposta" rodape={rodape}>
        <Aviso tom="sucesso" data-testid="pesquisa-respondida">
          {dados.situacao === "respondida" && !responder.isSuccess
            ? "Esta pesquisa já foi respondida. Obrigado!"
            : "Recebemos a sua resposta. Ela nos ajuda a melhorar o atendimento."}
        </Aviso>
      </CascaPublica>
    );
  }

  if (dados.situacao === "expirada") {
    return (
      <CascaPublica titulo="Este link expirou" rodape={rodape}>
        <Aviso tom="atencao" data-testid="pesquisa-expirada">
          O prazo para responder esta pesquisa acabou. Obrigado pelo interesse!
        </Aviso>
      </CascaPublica>
    );
  }

  return (
    <CascaPublica titulo="Como foi o atendimento?" descricao={dados.pergunta} rodape={rodape}>
      <form
        data-testid="formulario-da-pesquisa"
        noValidate
        onSubmit={(evento) => {
          evento.preventDefault();
          if (nota !== null) responder.mutate({ nota, comentario });
        }}
      >
        <fieldset className="mb-3">
          <legend className="campo__rotulo">Sua nota</legend>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Notas">
            {escalaDa(dados.tipo).map((valor) => (
              <button
                key={valor}
                type="button"
                className={`botao botao--pequeno${nota === valor ? "" : " botao--fantasma"}`}
                aria-pressed={nota === valor}
                aria-label={`Nota ${valor}`}
                onClick={() => setNota(valor)}
              >
                {valor}
              </button>
            ))}
          </div>
          <p className="nota-rodape mt-2 flex justify-between">
            <span>{dados.escala_min} · {dados.rotulo_min}</span>
            <span>{dados.escala_max} · {dados.rotulo_max}</span>
          </p>
        </fieldset>
        <CampoLongo
          rotulo="Quer deixar um comentário? (opcional)"
          value={comentario}
          maxLength={2000}
          onChange={(evento) => setComentario(evento.target.value)}
        />
        {responder.isError ? (
          <Aviso tom="erro" className="mb-3">
            {responder.error instanceof ErroDaApi
              ? responder.error.message
              : "Não foi possível registrar a sua resposta agora."}
          </Aviso>
        ) : null}
        <Botao type="submit" disabled={nota === null || responder.isPending} data-testid="enviar-resposta">
          {responder.isPending ? "Enviando…" : "Enviar resposta"}
        </Botao>
      </form>
    </CascaPublica>
  );
}
